import { useCallback, useMemo } from 'react';
import { IconTrashX } from '@tabler/icons-react';
import type { TableColumnType } from 'antd';
import { useIntl, type FormatDateOptions } from 'react-intl';

import type { ApiKey } from '@/modules/api/codegen';
import { useConfirmationModal } from '@/modules/modals';
import { Button, ButtonType } from '@/modules/ui';
import { visibleOnRowHoverClassName } from '@/modules/ui/components/Table/Table.rules';

import { useRevokeApiKey } from '../../../hooks';
import * as felaRules from '../ApiKeysTable.rules';
import type { ApiKeysSort, ApiKeysSortField } from './useApiKeysTableState';

const dateFormat: FormatDateOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

export const useApiKeysTableColumns = (sort: ApiKeysSort) => {
    const { formatMessage, formatDate } = useIntl();
    const { openConfirmationModal } = useConfirmationModal();
    // `useMutation` returns a fresh object every render, so depend on the bound `mutateAsync`
    // rather than the mutation itself — otherwise the memos below rebuild on every render.
    const { mutateAsync: revokeApiKey } = useRevokeApiKey();

    const confirmRevoke = useCallback(
        (apiKey: ApiKey) => {
            openConfirmationModal({
                title: formatMessage({ id: 'api-keys.revoke.title' }),
                content: formatMessage({ id: 'api-keys.revoke.content' }, { name: apiKey.name }),
                confirmButtonText: formatMessage({ id: 'api-keys.revoke.confirm' }),
                cancelButtonText: formatMessage({ id: 'api-keys.revoke.cancel' }),
                onConfirm: async () => {
                    await revokeApiKey({ pathParams: { apiKeyId: apiKey.id } });
                },
            });
        },
        [openConfirmationModal, formatMessage, revokeApiKey],
    );

    return useMemo(() => {
        const sortOrderOf = (field: ApiKeysSortField) => (sort.field === field ? sort.order : null);

        return (
            [
                {
                    dataIndex: 'name',
                    sorter: true,
                    sortOrder: sortOrderOf('name'),
                    ellipsis: true,
                },
                {
                    dataIndex: 'maskedKey',
                    width: 140,
                    ellipsis: true,
                },
                {
                    dataIndex: 'createdAt',
                    width: 140,
                    sorter: true,
                    sortOrder: sortOrderOf('createdAt'),
                    render: value => formatDate(value, dateFormat),
                },
                {
                    dataIndex: 'lastUsedAt',
                    width: 140,
                    sorter: true,
                    sortOrder: sortOrderOf('lastUsedAt'),
                    render: value => (value ? formatDate(value, dateFormat) : '—'),
                },
                {
                    key: 'actions',
                    render: (_, record) => (
                        <Button
                            type={ButtonType.TEXT}
                            color='buttonsDestructive'
                            customStyle={felaRules.revokeButton}
                            className={visibleOnRowHoverClassName}
                            icon={<IconTrashX size={16} />}
                            aria-label={formatMessage({ id: 'api-keys.table.actions.deleteKey' }, { name: record.name })}
                            onClick={() => confirmRevoke(record)}
                        >
                            {formatMessage({ id: 'api-keys.table.actions.delete' })}
                        </Button>
                    ),
                },
            ] as const satisfies TableColumnType<ApiKey>[]
        ).map(column => ({
            ...column,
            title: 'dataIndex' in column ? formatMessage({ id: `api-keys.table.columns.${column.dataIndex}` }) : undefined,
        }));
    }, [formatMessage, formatDate, confirmRevoke, sort]);
};
